import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDateTime, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, ChevronDown, ChevronRight, Users, Lock, X as XIcon, Send, Eye, CalendarPlus, Pencil, CopyPlus, Archive } from "lucide-react";

const RECOMMENDED_FALLBACK = [
  { value: "enablement-sprint", label: "Payments Optimization Sprint" },
  { value: "studio-retainer", label: "Studio Retainer" },
];

function labelForService(s) {
  if (s.quote_only) return `${s.title} — custom quote`;
  const price = fmtUSD(s.price);
  return s.recurring ? `${s.title} (${price}/mo)` : `${s.title} (${price})`;
}

const EMPTY_FORM = {
  client_name: "",
  client_email: "",
  company_name: "",
  project_title: "",
  scope: "",
  deliverables_text: "",
  expires_at: "",
  selections: [{ sku: "studio-retainer", role: "recommended", notes: "" }],
};

const ROLE_OPTIONS = [
  { value: "recommended", label: "Recommended" },
  { value: "alternative", label: "Alternative" },
  { value: "add_on", label: "Add-on" },
];

function AcceptanceLog({ quoteId }) {
  const [rows, setRows] = useState(null);
  const [clicks, setClicks] = useState(null);
  useEffect(() => {
    api.get(`/admin/project-quotes/${quoteId}/acceptances`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));
    api.get(`/admin/project-quotes/${quoteId}/cta-clicks`)
      .then((r) => setClicks(r.data))
      .catch(() => setClicks([]));
  }, [quoteId]);
  if (rows === null) return <div className="text-xs text-muted-foreground p-3">Loading…</div>;
  return (
    <div className="space-y-6">
      <div>
        <div className="label-mono text-xs mb-2">NDA acceptances</div>
        {rows.length === 0 ? (
          <div className="text-xs text-muted-foreground p-3 bg-white rounded-md border border-border" data-testid={`quote-acceptances-empty-${quoteId}`}>
            No NDA acceptances yet for this quote.
          </div>
        ) : (
          <table className="w-full text-xs bg-white rounded-md border border-border" data-testid={`quote-acceptances-${quoteId}`}>
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 whitespace-nowrap">{fmtDateTime(r.accepted_at)}</td>
                  <td className="p-3">{r.accepted_by_name}</td>
                  <td className="p-3">{r.accepted_by_email}</td>
                  <td className="p-3 font-mono text-muted-foreground">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div>
        <div className="label-mono text-xs mb-2">Accept-Quote clicks (final scoping call)</div>
        {(clicks || []).length === 0 ? (
          <div className="text-xs text-muted-foreground p-3 bg-white rounded-md border border-border" data-testid={`quote-cta-clicks-empty-${quoteId}`}>
            No Accept-Quote clicks yet.
          </div>
        ) : (
          <table className="w-full text-xs bg-white rounded-md border border-border" data-testid={`quote-cta-clicks-${quoteId}`}>
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {clicks.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="p-3 whitespace-nowrap">{fmtDateTime(c.clicked_at)}</td>
                  <td className="p-3 font-mono text-muted-foreground">{c.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SelectionRows({ selections, services, options, onChange }) {
  const set = (i, k, v) => {
    const next = selections.map((row, idx) => (idx === i ? { ...row, [k]: v } : row));
    // Ensure only ONE row is marked recommended at any time.
    if (k === "role" && v === "recommended") {
      next.forEach((row, idx) => {
        if (idx !== i && row.role === "recommended") row.role = "alternative";
      });
    }
    onChange(next);
  };
  const remove = (i) => onChange(selections.filter((_, idx) => idx !== i));
  const add = (role) => {
    // Duplicate SKUs across roles are allowed — an admin may offer the same
    // engagement (e.g. Studio Retainer) as both recommended and alternative
    // with different scoping notes. We just default the new row to the first
    // available service; the admin can change it inline.
    const nextSku = (services || [])[0]?.sku || options[0]?.value || "";
    if (!nextSku) return;
    // _rowKey is a client-only stable identifier so React can track rows through
    // reorders/removes without conflating them via array index. Never sent to the API.
    const rowKey = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `row-${Date.now()}-${Math.random()}`;
    onChange([...selections, { sku: nextSku, role, notes: "", _rowKey: rowKey }]);
  };
  return (
    <div className="grid gap-3" data-testid="quote-form-selections">
      {selections.map((row, i) => (
        <div
          key={row._rowKey || `initial-${i}-${row.sku}`}
          className="rounded-lg border border-border bg-ice-soft p-4 grid gap-3"
          data-testid={`quote-form-selection-${i}`}
        >
          <div className="grid grid-cols-[1fr_180px_auto] gap-3 items-end">
            <div className="grid gap-1">
              <Label className="text-xs">Service</Label>
              <select
                value={row.sku}
                onChange={(e) => set(i, "sku", e.target.value)}
                className="rounded-md border border-border bg-white px-3 py-2 text-sm"
                data-testid={`quote-form-selection-sku-${i}`}
              >
                {options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs">Role</Label>
              <select
                value={row.role}
                onChange={(e) => set(i, "role", e.target.value)}
                className="rounded-md border border-border bg-white px-3 py-2 text-sm"
                data-testid={`quote-form-selection-role-${i}`}
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              disabled={selections.length === 1}
              className="text-muted-foreground hover:text-destructive"
              data-testid={`quote-form-selection-remove-${i}`}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid gap-1">
            <Label className="text-xs">Notes (shown under this card on the quote — admin commentary)</Label>
            <Textarea
              rows={2}
              value={row.notes}
              onChange={(e) => set(i, "notes", e.target.value)}
              placeholder={
                row.role === "recommended"
                  ? "Why this engagement is the right fit for this client..."
                  : row.role === "alternative"
                  ? "Why this is offered as an alternative..."
                  : "Why this add-on would layer well on top..."
              }
              data-testid={`quote-form-selection-notes-${i}`}
            />
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => add("alternative")} data-testid="quote-form-add-alternative">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add alternative
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => add("add_on")} data-testid="quote-form-add-addon">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add add-on
        </Button>
      </div>
    </div>
  );
}

function CreateQuoteDialog({ open, onOpenChange, onCreated, onUpdated, services, editingQuote }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editingQuote;

  // Pre-fill the form whenever we open in edit-mode; reset on close/create.
  useEffect(() => {
    if (!open) return;
    if (editingQuote) {
      setForm({
        client_name: editingQuote.client_name || "",
        client_email: editingQuote.client_email || "",
        company_name: editingQuote.company_name || "",
        project_title: editingQuote.project_title || "",
        scope: editingQuote.scope || "",
        deliverables_text: (editingQuote.deliverables || []).join("\n"),
        expires_at: editingQuote.expires_at || "",
        selections: (editingQuote.selections && editingQuote.selections.length > 0)
          ? editingQuote.selections.map((s) => ({
              sku: s.sku,
              role: s.role,
              notes: s.notes || "",
              _rowKey: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `row-${Math.random()}`,
            }))
          : [{
              sku: editingQuote.recommended_sku || "studio-retainer",
              role: "recommended",
              notes: "",
              _rowKey: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `row-${Math.random()}`,
            }],
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, editingQuote]);

  const options = useMemo(() => {
    if (!services || services.length === 0) return RECOMMENDED_FALLBACK;
    return services.map((s) => ({ value: s.sku, label: labelForService(s) }));
  }, [services]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (sendEmail) => {
    const deliverables = form.deliverables_text
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!form.client_name.trim() || !form.client_email.includes("@") || !form.project_title.trim() || !form.scope.trim()) {
      toast.error("Client name, email, project title, and scope are required");
      return;
    }
    const recommendedCount = form.selections.filter((s) => s.role === "recommended").length;
    if (recommendedCount !== 1) {
      toast.error("Exactly one selection must be marked Recommended");
      return;
    }
    for (const s of form.selections) {
      if (!s.sku) { toast.error("Every selection must have a service chosen"); return; }
    }
    // Duplicate SKUs across roles are permitted on purpose (e.g. Studio
    // Retainer offered as both recommended and alternative with different
    // scoping notes). No client-side dedupe.
    setSubmitting(true);
    try {
      const body = {
        client_name: form.client_name.trim(),
        client_email: form.client_email.trim().toLowerCase(),
        company_name: form.company_name.trim() || null,
        project_title: form.project_title.trim(),
        scope: form.scope.trim(),
        deliverables,
        expires_at: form.expires_at || null,
        selections: form.selections.map((s) => ({
          sku: s.sku,
          role: s.role,
          notes: s.notes.trim() || null,
        })),
      };

      if (isEditing) {
        const { data } = await api.patch(`/admin/project-quotes/${editingQuote.id}`, body);
        if (sendEmail) {
          const { data: sendData } = await api.post(`/admin/project-quotes/${editingQuote.id}/send-email`);
          toast.success("Changes saved — client email re-sent");
          onUpdated({ ...data, status: sendData.status, email_sent_at: sendData.email_sent_at });
        } else {
          toast.success("Changes saved");
          onUpdated(data);
        }
      } else {
        const { data } = await api.post("/admin/project-quotes", { ...body, send_email: sendEmail });
        if (sendEmail) {
          toast.success("Quote created — share link emailed to client");
          try { await navigator.clipboard.writeText(data.share_url); } catch { /* clipboard not available */ }
        } else {
          toast.success("Draft saved — opening preview in a new tab");
          // Open the admin preview (share URL) so the admin can review the
          // client-facing render before dispatching the email.
          try { window.open(`/quote/${data.id}`, "_blank", "noopener,noreferrer"); } catch { /* popup blocked */ }
        }
        onCreated(data);
      }
      onOpenChange(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEditing ? "Failed to save changes" : "Failed to create quote"));
    } finally {
      setSubmitting(false);
    }
  };

  const isSentEdit = isEditing && editingQuote?.status !== "draft" && editingQuote?.email_sent_at;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-quote-dialog">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit project quote" : "New project quote"}</DialogTitle>
        </DialogHeader>
        {isSentEdit && (
          <div
            className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
            data-testid="quote-edit-sent-warning"
          >
            <strong>Heads up:</strong> this quote has already been sent to the client. The share
            link stays the same — anyone who has it will see the updated content on their next refresh.
          </div>
        )}
        <div className="grid gap-4 mt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Contact name <span className="text-muted-foreground text-xs">(&ldquo;Prepared for&rdquo;)</span></Label>
              <Input value={form.client_name} onChange={set("client_name")} placeholder="Jane Doe" data-testid="quote-form-client-name" />
            </div>
            <div className="grid gap-2">
              <Label>Contact email</Label>
              <Input type="email" value={form.client_email} onChange={set("client_email")} placeholder="jane@acme.com" data-testid="quote-form-client-email" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Company name <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Input
              value={form.company_name}
              onChange={set("company_name")}
              placeholder="Acme Corporation Inc."
              data-testid="quote-form-company-name"
            />
          </div>
          <div className="grid gap-2">
            <Label>Project title</Label>
            <Input value={form.project_title} onChange={set("project_title")} placeholder="Payments stack optimization" data-testid="quote-form-title" />
          </div>
          <div className="grid gap-2">
            <Label>Scope (separate paragraphs with a blank line)</Label>
            <Textarea rows={6} value={form.scope} onChange={set("scope")} placeholder="Paragraph 1...&#10;&#10;Paragraph 2..." data-testid="quote-form-scope" />
          </div>
          <div className="grid gap-2">
            <Label>Deliverables (one per line)</Label>
            <Textarea rows={5} value={form.deliverables_text} onChange={set("deliverables_text")} placeholder="Cost-of-acceptance map&#10;Vendor renegotiation playbook&#10;90-day execution plan" data-testid="quote-form-deliverables" />
          </div>
          <div className="grid gap-2">
            <Label>Link expires (optional, defaults to +14 days)</Label>
            <Input
              type="date"
              value={form.expires_at}
              onChange={set("expires_at")}
              data-testid="quote-form-expires-at"
            />
          </div>
          <div className="grid gap-2">
            <Label>Engagement options</Label>
            <p className="text-xs text-muted-foreground">
              Mark exactly one as <strong>Recommended</strong>. Add any number of <strong>Alternatives</strong> (other shapes) and <strong>Add-ons</strong> (extra layers). Each row gets an admin notes block that renders under the card.
            </p>
            <SelectionRows
              selections={form.selections}
              services={services}
              options={options}
              onChange={(next) => setForm({ ...form, selections: next })}
            />
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="outline"
            onClick={() => submit(false)}
            disabled={submitting}
            data-testid="quote-form-save-draft"
          >
            <Eye className="h-4 w-4 mr-1.5" />
            {submitting ? "Saving…" : isEditing ? "Save changes" : "Save as draft (preview)"}
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={submitting}
            className="bg-[#ac62ea] hover:bg-[#9851d6] text-white"
            data-testid="quote-form-submit"
          >
            <Send className="h-4 w-4 mr-1.5" />
            {submitting
              ? "Sending…"
              : isEditing
              ? (isSentEdit ? "Save & re-send" : "Save & send now")
              : "Save & send now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status, emailSentAt, expired }) {
  const raw = status || (emailSentAt ? "sent" : "draft");
  // Treat client-derived expiration as authoritative for the pill so the
  // admin sees "Expired" even before the backend re-persists the status.
  const effective = expired && raw !== "accepted" ? "expired" : raw;
  const map = {
    draft: { label: "Draft", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    accepted: { label: "Accepted", cls: "bg-[#ac62ea]/10 text-[#ac62ea] border-[#ac62ea]/30" },
    expired: { label: "Expired", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  };
  const cfg = map[effective] || map.sent;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full text-xs border ${cfg.cls}`}
      data-testid={`quote-status-${effective}`}
    >
      {cfg.label}
    </span>
  );
}

// Client-side mirror of the backend `_quote_link_expired` helper. `expires_at`
// is stored as a YYYY-MM-DD string with end-of-day (UTC) semantics.
function isQuoteExpired(q) {
  if (!q?.expires_at || q.status === "accepted") return false;
  try {
    const end = new Date(`${q.expires_at}T23:59:59Z`);
    return new Date() > end;
  } catch {
    return false;
  }
}

function QuoteRow({ q, expanded, onToggle, onUpdated, onEdit, onDuplicated }) {
  const recommendedLabel = q.recommended_sku === "studio-retainer" ? "Studio Retainer" : "Payments Optimization Sprint";
  const shareUrl = `${window.location.origin}/quote/${q.id}`;
  const [sending, setSending] = useState(false);
  const [extending, setExtending] = useState(false);
  const effectiveStatus = q.status || (q.email_sent_at ? "sent" : "draft");
  const isDraft = effectiveStatus === "draft";
  const isAccepted = effectiveStatus === "accepted";
  const expired = isQuoteExpired(q);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/project-quotes/${q.id}/send-email`);
      toast.success(isDraft ? "Draft dispatched — client email sent" : "Client email re-sent");
      onUpdated({ ...q, status: data.status, email_sent_at: data.email_sent_at });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const extendExpiry = async () => {
    setExtending(true);
    try {
      const { data } = await api.post(`/admin/project-quotes/${q.id}/extend-expiry`);
      toast.success(`Link extended — new expiry ${data.expires_at}`);
      onUpdated({ ...q, expires_at: data.expires_at, status: data.status });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to extend expiry");
    } finally {
      setExtending(false);
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/admin/project-quotes/${q.id}/duplicate`);
      toast.success("Cloned to a new draft — opening preview");
      onDuplicated(data);
      try { window.open(`/quote/${data.id}`, "_blank", "noopener,noreferrer"); } catch { /* popup blocked */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate quote");
    }
  };

  return (
    <>
      <tr className="border-b border-border" data-testid={`quote-row-${q.id}`}>
        <td className="p-4 text-left align-top">
          <button
            type="button"
            onClick={onToggle}
            className="inline-flex items-center gap-2 text-left hover:text-[#ac62ea]"
            data-testid={`quote-row-toggle-${q.id}`}
          >
            {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
            <span className="font-medium text-left">{q.project_title}</span>
          </button>
          <div className="text-xs text-muted-foreground mt-1 font-mono text-left">{q.id}</div>
        </td>
        <td className="p-4 text-left align-top">
          <div>{q.client_name}</div>
          <div className="text-xs text-muted-foreground">{q.client_email}</div>
          {q.company_name && (
            <div className="text-xs text-muted-foreground mt-1 italic" data-testid={`quote-company-${q.id}`}>
              {q.company_name}
            </div>
          )}
        </td>
        <td className="p-4 text-xs text-left align-top">
          <div className="flex flex-col gap-1.5">
            <StatusPill status={q.status} emailSentAt={q.email_sent_at} expired={expired} />
            <span className="inline-block px-2 py-0.5 rounded-full bg-[#ac62ea]/10 text-[#ac62ea] border border-[#ac62ea]/30 w-fit">
              {recommendedLabel}
            </span>
          </div>
        </td>
        <td className="p-4 text-muted-foreground whitespace-nowrap text-xs text-left align-top">{fmtDateTime(q.created_at)}</td>
        <td className="p-4 text-xs text-left align-top whitespace-nowrap" data-testid={`quote-expires-${q.id}`}>
          {q.expires_at ? (
            <span className={expired ? "text-rose-700 font-medium" : "text-foreground/70"}>
              {q.expires_at}
              {expired ? " · EXPIRED" : ""}
            </span>
          ) : <span className="text-muted-foreground">—</span>}
        </td>
        <td className="p-4 text-right align-top">
          <div className="flex gap-2 justify-end flex-wrap">
            {expired && !isAccepted ? (
              <Button
                size="sm"
                className="rounded-full bg-[#ac62ea] hover:bg-[#9851d6] text-white"
                onClick={extendExpiry}
                disabled={extending}
                data-testid={`quote-extend-expiry-${q.id}`}
              >
                <CalendarPlus className="h-3 w-3 mr-1" /> {extending ? "Extending…" : "Extend +14d"}
              </Button>
            ) : !expired && isDraft ? (
              <Button
                size="sm"
                className="rounded-full bg-[#ac62ea] hover:bg-[#9851d6] text-white"
                onClick={sendEmail}
                disabled={sending}
                data-testid={`quote-send-email-${q.id}`}
              >
                <Send className="h-3 w-3 mr-1" /> {sending ? "Sending…" : "Email now"}
              </Button>
            ) : !expired ? (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={sendEmail}
                disabled={sending}
                data-testid={`quote-resend-email-${q.id}`}
              >
                <Send className="h-3 w-3 mr-1" /> {sending ? "Sending…" : "Resend"}
              </Button>
            ) : null}
            {!isAccepted && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() => onEdit(q)}
                data-testid={`quote-edit-${q.id}`}
              >
                <Pencil className="h-3 w-3 mr-1" /> Edit
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={duplicate}
              data-testid={`quote-duplicate-${q.id}`}
              title="Clone into a new draft with a fresh ID + 14-day expiry"
            >
              <CopyPlus className="h-3 w-3 mr-1" /> Duplicate
            </Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={copy} data-testid={`quote-copy-${q.id}`}>
              <Copy className="h-3 w-3 mr-1" /> Copy link
            </Button>
            <a href={`/quote/${q.id}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="rounded-full" data-testid={`quote-open-${q.id}`}>
                <Eye className="h-3 w-3 mr-1" /> {isDraft ? "Preview" : "Open"}
              </Button>
            </a>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-ice-soft" data-testid={`quote-row-expanded-${q.id}`}>
          <td colSpan={6} className="p-4">
            <div className="flex items-center gap-2 text-xs label-mono mb-2">
              <Users className="h-3 w-3" /> NDA acceptance log
            </div>
            <AcceptanceLog quoteId={q.id} />
          </td>
        </tr>
      )}
    </>
  );
}

export default function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/project-quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([]));
  }, []);

  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, [load]);

  const onCreated = (newQuote) => {
    setQuotes((prev) => [{ ...newQuote }, ...prev.filter((p) => p.id !== newQuote.id)]);
  };

  const onUpdated = (updated) => {
    setQuotes((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
  };

  const onDuplicated = (clone) => {
    setQuotes((prev) => [clone, ...prev]);
  };

  const [backfilling, setBackfilling] = useState(false);
  const backfillLegacyExpiry = async () => {
    if (!window.confirm("Immediately expire every quote that doesn't have an expiration date? (Accepted quotes are left alone. Safe to run more than once.)")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/project-quotes/backfill-legacy-expiry");
      if (data.count === 0) {
        toast.success("No legacy quotes to backfill — everything already has an expiry.");
      } else {
        toast.success(`Expired ${data.count} legacy quote${data.count === 1 ? "" : "s"} — refreshing…`);
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) setEditingQuote(null);
  };

  const startEdit = (quote) => {
    setEditingQuote(quote);
    setOpen(true);
  };

  return (
    <div className="mt-8" data-testid="admin-quotes-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-mono">Project quotes</div>
          <h2 className="font-display text-2xl mt-1 tracking-tight">NDA-gated quote links</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Create a quote → SideB auto-emails the client a private link. Recipients accept the Mutual NDA before scope and pricing are revealed. Every acceptance is logged below the row.
          </p>
        </div>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button
              className="rounded-full bg-[#ac62ea] hover:bg-[#9851d6] text-white"
              data-testid="quote-new-btn"
              onClick={() => setEditingQuote(null)}
            >
              <Plus className="h-4 w-4 mr-1" /> New quote
            </Button>
          </DialogTrigger>
          <CreateQuoteDialog
            open={open}
            onOpenChange={handleOpenChange}
            onCreated={onCreated}
            onUpdated={onUpdated}
            services={services}
            editingQuote={editingQuote}
          />
        </Dialog>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full ml-2"
          onClick={backfillLegacyExpiry}
          disabled={backfilling}
          title="One-time cleanup: expire every quote that has no expiry date. Safe to re-run."
          data-testid="quote-backfill-legacy-btn"
        >
          <Archive className="h-3.5 w-3.5 mr-1" /> {backfilling ? "Backfilling…" : "Backfill legacy expiry"}
        </Button>
      </div>

      <div className="card-sharp">
        <table className="w-full text-sm">
          <thead className="text-left border-b border-border">
            <tr className="text-xs uppercase tracking-widest text-muted-foreground">
              <th className="p-4 text-left">Project / ID</th>
              <th className="p-4 text-left">Recipient</th>
              <th className="p-4 text-left">Status / Recommended</th>
              <th className="p-4 text-left">Created</th>
              <th className="p-4 text-left">Expires</th>
              <th className="p-4 text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {quotes.length === 0 && (
              <tr><td colSpan={6} className="p-10 text-center text-muted-foreground" data-testid="quotes-empty">
                <Lock className="h-5 w-5 mx-auto mb-2 text-muted-foreground/60" />
                No quotes yet. Click <strong>New quote</strong> to create your first NDA-gated proposal link.
              </td></tr>
            )}
            {quotes.map((q) => (
              <QuoteRow
                key={q.id}
                q={q}
                expanded={expandedId === q.id}
                onToggle={() => setExpandedId(expandedId === q.id ? null : q.id)}
                onUpdated={onUpdated}
                onEdit={startEdit}
                onDuplicated={onDuplicated}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
